import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigConstants } from '../config/config-constants';
import { InputFieldPropertyModel } from '../models/low-level/input-field-property.model';
import { InitializableInterface } from './initializable.interface';

/**
 * Public contract of the input-field-properties service: the per-field validation/display
 * metadata (mandatory, readonly, min/max length, pattern, ...) loaded once from OData and
 * looked up by field name, screen name, and business process. Extends
 * {@link InitializableInterface} (`isInitialized` + `init()`).
 *
 * Implemented by both the legacy `InputFieldPropertiesService` and the NgRx-backed
 * `InputFieldPropertiesNgrxService`; consumers depend on this interface via
 * {@link InputFieldPropertiesInstanceToken} rather than on a concrete class.
 */
export interface InputFieldPropertiesInterface extends InitializableInterface {
    isInitialized: boolean;

    /** Load the input field properties from the backend (also re-run on sync completion). */
    init(): Observable<InputFieldPropertyModel[]>;

    /**
     * Get all properties of an input field defined by its name and the screen it is placed on.
     *
     * @param fieldName
     * @param screenName
     * @param process
     */
    getInputFieldProperties(
        fieldName: string,
        screenName: string,
        process: ConfigConstants.PROCESS_INDICATOR,
    ): InputFieldPropertyModel | undefined;
}

/**
 * DI token for {@link InputFieldPropertiesInterface}. Lets the app choose which implementation
 * to provide (legacy `InputFieldPropertiesService` or the NgRx-based
 * `InputFieldPropertiesNgrxService`) without consumers depending on a concrete class.
 */
export const InputFieldPropertiesInstanceToken = new InjectionToken<InputFieldPropertiesInterface>(
    'InputFieldPropertiesInterface',
);
